import { useMemo } from 'react';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip as ChartTooltip, CommonSeriesSettings, Point, Animation } from 'devextreme-react/chart';
import { LeveyJenningsReportRowDTO } from '../../types/types.qualityControl';
import { chartInstanceToPng, CapturedImage } from './qc-chart-capture';
import './levey-jennings-report-card.scss';

// ─── Colores de clasificación Sigma (alineados con el resto del módulo) ───
const SIGMA_COLORS: Record<string, string> = {
  CLASE_MUNDIAL: '#4CAF50',
  EXCELENTE: '#2196F3',
  BUENO: '#FFC107',
  MARGINAL: '#FF9800',
  INACEPTABLE: '#F44336',
};

const SIGMA_LABELS: Record<string, string> = {
  CLASE_MUNDIAL: 'Clase Mundial',
  EXCELENTE: 'Excelente',
  BUENO: 'Bueno',
  MARGINAL: 'Marginal',
  INACEPTABLE: 'Inaceptable',
};

const getSigmaColor = (clasif?: string | null): string => (clasif ? SIGMA_COLORS[clasif] || '#9E9E9E' : '#9E9E9E');
const getSigmaLabel = (clasif?: string | null): string => (clasif ? SIGMA_LABELS[clasif] || String(clasif) : 'N/D');

const formatFecha = (iso?: string): string => {
  if (!iso) return 'N/A';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

interface LeveyJenningsReportCardProps {
  row: LeveyJenningsReportRowDTO;
  mostrarLineas: boolean;
  onImageReady?: (configId: number, image: CapturedImage | null) => void;
}

const LeveyJenningsReportCard = ({ row, mostrarLineas, onImageReady }: LeveyJenningsReportCardProps) => {
  const controlName = row.MaterialNivel ? `${row.MaterialNombre} - ${row.MaterialNivel}` : row.MaterialNombre;
  const esCuant = row.TipoResultado !== 'CUALITATIVO';

  // Captura la gráfica a imagen apenas termina de dibujarse, para que el export
  // (PDF/Excel) use imágenes ya cacheadas sin depender de qué pestaña esté activa.
  const handleDrawn = (e: any) => {
    if (!onImageReady) return;
    chartInstanceToPng(e.component).then((img) => onImageReady(row.ConfiguracionId, img));
  };

  const media = row.Media || 0;
  const sd = row.DesviacionEstandar || 0;
  const cv = media > 0 ? ((sd / media) * 100).toFixed(2) : '0.00';
  const total = row.TotalMediciones || 0;
  const validadas = row.MedicionesValidadas || 0;
  const conViolaciones = row.MedicionesConViolaciones || 0;
  const conformidad = total > 0 ? ((validadas / total) * 100).toFixed(1) : '0.0';
  const aprobadas = row.MedicionesAprobadas || 0;
  const noAprobadas = row.MedicionesNoAprobadas || 0;
  const pctAprob = total > 0 ? ((aprobadas / total) * 100).toFixed(1) : '0.0';

  // Datos del gráfico cuantitativo (líneas SD + puntos)
  const chartData = useMemo(() => {
    if (!esCuant) return [];
    return (row.Mediciones || []).map((m, idx) => ({
      NumeroMedicion: idx + 1,
      Valor: m.Valor,
      Media: row.Linea_Media,
      SD1Positivo: row.Linea_1SD_Superior,
      SD1Negativo: row.Linea_1SD_Inferior,
      SD2Positivo: row.Linea_2SD_Superior,
      SD2Negativo: row.Linea_2SD_Inferior,
      SD3Positivo: row.Linea_3SD_Superior,
      SD3Negativo: row.Linea_3SD_Inferior,
      ColorPunto: m.ColorPunto || '#2196F3',
      Fecha: m.FechaHora,
      Usuario: m.UsuarioNombre || 'N/A',
      Nota: m.Nota || '',
      Reglas: (m.Violaciones || []).map((v) => v.Qcv_Regla).join(', '),
    }));
  }, [row, esCuant]);

  // Datos del gráfico cualitativo (aprobado/no aprobado)
  const cualData = useMemo(() => {
    if (esCuant) return [];
    return (row.Mediciones || []).map((m, idx) => ({
      NumeroMedicion: idx + 1,
      Aprobado: m.Aprobado === true ? 1 : 0,
      ValorCualitativo: m.ValorCualitativo || '',
      Fecha: m.FechaHora,
      Usuario: m.UsuarioNombre || 'N/A',
    }));
  }, [row, esCuant]);

  const sinDatos = (row.Mediciones || []).length === 0;

  return (
    <div className='lj-report-card chart-with-panel' style={{ marginBottom: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <h5 style={{ margin: 0, color: '#333', fontSize: '14px' }}>{row.AnalitoNombre} — {controlName}</h5>
        <span style={{ fontSize: '11px', color: '#888' }}>Lote: {row.LoteCodigo}{row.AnalizadorNombre ? ` · ${row.AnalizadorNombre}` : ''}</span>
        {row.TEa != null && row.SigmaActual != null && (
          <span className='sigma-inline-chip' style={{ backgroundColor: getSigmaColor(row.ClasificacionSigma) }}>
            σ {row.SigmaActual.toFixed(2)} · {getSigmaLabel(row.ClasificacionSigma)}
          </span>
        )}
      </div>

      <div className='chart-layout-row'>
        {/* Gráfico */}
        <div className='chart-area'>
          {sinDatos ? (
            <div className='no-chart-data'>
              <p>No hay mediciones de {controlName} en el período seleccionado</p>
            </div>
          ) : !esCuant ? (
            <Chart
              dataSource={cualData}
              height={360}
              onDrawn={handleDrawn}
              customizePoint={(pt: any) => {
                if (pt.seriesName === 'Resultado') {
                  const color = pt.data.Aprobado === 1 ? '#4CAF50' : '#F44336';
                  return { color, hoverStyle: { color } };
                }
                return {};
              }}>
              <ArgumentAxis title='Número de Medición' />
              <ValueAxis title='' visualRange={[-0.5, 1.5]} />
              <Animation enabled={false} />
              <CommonSeriesSettings argumentField='NumeroMedicion' />
              <Series valueField='Aprobado' name='Resultado' type='scatter'>
                <Point size={14} visible={true} symbol='circle' />
              </Series>
              <Legend verticalAlignment='bottom' horizontalAlignment='center' />
              <ChartTooltip
                enabled={true}
                shared={false}
                zIndex={10000}
                customizeTooltip={(pt: any) => {
                  if (pt.seriesName === 'Resultado') {
                    const d = pt.point.data;
                    return { html: `<div style="padding:8px;"><div><strong>Valor:</strong> ${d.ValorCualitativo}</div><div><strong>Resultado:</strong> ${d.Aprobado === 1 ? 'APROBADO' : 'NO APROBADO'}</div><div><strong>Fecha:</strong> ${formatFecha(d.Fecha)}</div><div><strong>Usuario:</strong> ${d.Usuario}</div></div>` };
                  }
                  return { text: pt.valueText };
                }}
              />
            </Chart>
          ) : (
            <Chart
              dataSource={chartData}
              height={400}
              onDrawn={handleDrawn}
              customizePoint={(pt: any) => {
                if (pt.seriesName === 'Mediciones') {
                  const colorPunto = pt.data.ColorPunto;
                  if (colorPunto) return { color: colorPunto, hoverStyle: { color: colorPunto } };
                }
                return {};
              }}>
              <ArgumentAxis title='Número de Medición' />
              <ValueAxis title='Valor' />
              <Animation enabled={false} />
              <CommonSeriesSettings argumentField='NumeroMedicion' />
              <Series valueField='Media' name='Media' color='#66BB6A' type='line' dashStyle='longDash' width={2} opacity={0.6}><Point visible={false} /></Series>
              <Series valueField='SD1Positivo' name='+1 SD' color='#FFD54F' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
              <Series valueField='SD1Negativo' name='-1 SD' color='#FFD54F' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
              <Series valueField='SD2Positivo' name='+2 SD' color='#FFB74D' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
              <Series valueField='SD2Negativo' name='-2 SD' color='#FFB74D' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
              <Series valueField='SD3Positivo' name='+3 SD' color='#EF9A9A' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
              <Series valueField='SD3Negativo' name='-3 SD' color='#EF9A9A' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
              <Series valueField='Valor' name='Mediciones' color='#2196F3' type={mostrarLineas ? 'line' : 'scatter'} width={mostrarLineas ? 1.5 : 0} hoverMode='onlyPoint'>
                <Point size={11} visible={true} hoverMode='onlyPoint' symbol='circle' />
              </Series>
              <Legend verticalAlignment='bottom' horizontalAlignment='center' />
              <ChartTooltip
                enabled={true}
                shared={false}
                zIndex={10000}
                customizeTooltip={(pt: any) => {
                  if (pt.seriesName === 'Mediciones') {
                    const d = pt.point.data;
                    const reglas = d.Reglas ? `<div><strong>Violaciones:</strong> ${d.Reglas}</div>` : '';
                    const nota = d.Nota ? `<div><strong>Nota:</strong> ${d.Nota}</div>` : '';
                    return { html: `<div style="padding:8px;"><div><strong>Valor:</strong> ${pt.valueText}</div><div><strong>Fecha:</strong> ${formatFecha(d.Fecha)}</div><div><strong>Usuario:</strong> ${d.Usuario}</div>${reglas}${nota}</div>` };
                  }
                  return { text: pt.valueText };
                }}
              />
            </Chart>
          )}
        </div>

        {/* Panel lateral */}
        <div className='chart-side-panel'>
          {esCuant ? (
            <>
              <div className='side-panel-card'>
                <div className='side-panel-title'>⚙ Estado del Control</div>
                <div className='conformidad-valor' style={{ color: parseFloat(conformidad) >= 80 ? '#4CAF50' : parseFloat(conformidad) >= 50 ? '#FF9800' : '#F44336' }}>{conformidad}%</div>
                <div className='conformidad-label'>Aceptabilidad</div>
                <div className='estado-items'>
                  <div className='estado-item'><span className='estado-dot' style={{ background: '#4CAF50' }}></span><span>Validadas</span><strong>{validadas}</strong></div>
                  <div className='estado-item'><span className='estado-dot' style={{ background: '#F44336' }}></span><span>Con violaciones</span><strong>{conViolaciones}</strong></div>
                  <div className='estado-item'><span className='estado-dot' style={{ background: '#2196F3' }}></span><span>Total</span><strong>{total}</strong></div>
                </div>
              </div>
              <div className='side-panel-card'>
                <div className='side-panel-title'>⚠ Límites de Control</div>
                <div className='limites-grid'>
                  <div className='limite-row'><span>Media:</span><strong>{media.toFixed(2)}</strong></div>
                  <div className='limite-row'><span>SD:</span><strong>{sd.toFixed(2)}</strong></div>
                  <div className='limite-row'><span>CV:</span><strong>{cv}%</strong></div>
                  <div className='limite-row sd-row' style={{ color: '#FFC107' }}><span>±1SD:</span><strong>{row.Linea_1SD_Inferior?.toFixed(2)} - {row.Linea_1SD_Superior?.toFixed(2)}</strong></div>
                  <div className='limite-row sd-row' style={{ color: '#FF9800' }}><span>±2SD:</span><strong>{row.Linea_2SD_Inferior?.toFixed(2)} - {row.Linea_2SD_Superior?.toFixed(2)}</strong></div>
                  <div className='limite-row sd-row' style={{ color: '#F44336' }}><span>±3SD:</span><strong>{row.Linea_3SD_Inferior?.toFixed(2)} - {row.Linea_3SD_Superior?.toFixed(2)}</strong></div>
                </div>
              </div>
            </>
          ) : (
            <div className='side-panel-card'>
              <div className='side-panel-title'>⚙ Estado del Control</div>
              <div className='conformidad-valor' style={{ color: parseFloat(pctAprob) >= 80 ? '#4CAF50' : parseFloat(pctAprob) >= 50 ? '#FF9800' : '#F44336' }}>{pctAprob}%</div>
              <div className='conformidad-label'>Aprobación</div>
              <div className='estado-items'>
                <div className='estado-item'><span className='estado-dot' style={{ background: '#4CAF50' }}></span><span>Aprobadas</span><strong>{aprobadas}</strong></div>
                <div className='estado-item'><span className='estado-dot' style={{ background: '#F44336' }}></span><span>No aprobadas</span><strong>{noAprobadas}</strong></div>
                <div className='estado-item'><span className='estado-dot' style={{ background: '#2196F3' }}></span><span>Total</span><strong>{total}</strong></div>
              </div>
            </div>
          )}

          {/* Violaciones Westgard */}
          <div className={`side-panel-card ${row.TotalRechazos > 0 || conViolaciones > 0 ? 'violations' : 'no-violations'}`}>
            <div className='side-panel-title'>⚠ Violaciones Westgard</div>
            {row.ResumenViolaciones && row.ResumenViolaciones.length > 0 ? (
              <div className='limites-grid'>
                {row.ResumenViolaciones.map((v) => (
                  <div className='limite-row' key={v.Regla}><span>{v.Regla}:</span><strong style={{ color: '#F44336' }}>{v.Conteo}</strong></div>
                ))}
                <div className='limite-row' style={{ borderTop: '1px solid #eee', marginTop: 4, paddingTop: 4 }}><span>Rechazos:</span><strong style={{ color: row.TotalRechazos > 0 ? '#F44336' : '#4CAF50' }}>{row.TotalRechazos}</strong></div>
              </div>
            ) : (
              <div className='violation-text' style={{ color: '#4CAF50' }}>✓ Sin violaciones en el período</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeveyJenningsReportCard;
